import {useEffect, useRef} from 'react';

type UseInfiniteScrollOptions = {
  hasNextPage: boolean;
  isFetching: boolean;
  isError: boolean;
  fetchNextPage: () => void;
  // How far ahead of the sentinel to start loading, so the next page is ready on arrival.
  rootMargin?: string;
};

export function useInfiniteScroll({
  hasNextPage,
  isFetching,
  isError,
  fetchNextPage,
  rootMargin = '600px',
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    // Stop auto-loading on error, or a failing page would refetch in a tight loop.
    if (!sentinel || !hasNextPage || isFetching || isError) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPageRef.current();
      },
      {rootMargin},
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetching, isError, rootMargin]);

  return sentinelRef;
}
